/**
 * Merge a defaults object into a previous state object, but only for keys
 * whose previous value is `undefined`. Existing values — including empty
 * strings, `null`, `0`, and `false` — are preserved as-is.
 *
 * The use case is "fill the gaps without clobbering user input": effects that
 * compute default values for a form should be safe to re-run without
 * overwriting fields the user has explicitly cleared. An empty string from a
 * user-cleared field stays empty; `null` from an explicit blanking stays null;
 * only keys the user has never touched receive the computed default.
 *
 * Always returns a new object reference so React `setState` consumers trigger
 * a re-render even if no values changed.
 *
 * @template T
 * @param {T} prev - The current state object. Existing values are preserved.
 * @param {Partial<T>} defaults - Default values to fill in for missing /
 *   `undefined` keys only.
 * @returns {T} A new object combining `prev` with `defaults` filled in.
 */
export function mergeFillIfUndefined(prev, defaults) {
    const merged = { ...prev };
    for (const [k, v] of Object.entries(defaults || {})) {
        if (merged[k] === undefined) merged[k] = v;
    }
    return merged;
}

export default mergeFillIfUndefined;
